import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import {
  CreateMediaInput,
  JobEntityStatusEnum,
  JobInputPropsInput,
  JobPurposeEnum,
} from '@/types/schema';

import { JobContentFragment } from '../../graphql/fragments/generated/JobContent';
import { useCreateImportJobMutation } from '../../graphql/mutations/generated/createImportJob';
import { BulkImportFileTypes } from '../../types';
import { getImportJobName } from '../../utils';
import useJobs from '../useJobs';
import useMediaUpload from '../useMediaUpload';
import { ImportJobType } from './types';

interface JobsParams {
  purpose: JobPurposeEnum;
  jobInputProps?: JobInputPropsInput;
  getLatestJob?: (jobs?: JobContentFragment[]) => JobContentFragment | undefined;
  fileType?: BulkImportFileTypes;
  referenceId?: string;
}

export const IN_PROGRESS_JOBS_STATUS = [
  JobEntityStatusEnum.NotStarted,
  JobEntityStatusEnum.Parsing,
  JobEntityStatusEnum.Validating,
  JobEntityStatusEnum.InProcess,
];

const useImportProps = (params: JobsParams) => {
  const {
    jobInputProps,
    purpose,
    getLatestJob,
    fileType = BulkImportFileTypes.CSV,
    referenceId,
  } = params;
  const { showErrorSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [, executeCreateImportJob] = useCreateImportJobMutation();
  const { t } = useTranslation();

  const { jobs: jobsData, refetch: refetchJobs } = useJobs({
    purpose: [purpose],
    status: IN_PROGRESS_JOBS_STATUS,
  });
  const latestJob = useMemo(
    () => (getLatestJob ? getLatestJob(jobsData) : jobsData?.[0]),
    [getLatestJob, jobsData],
  );

  const [currentImportJob, setCurrentImportJob] = useState<ImportJobType | undefined>();

  useEffect(() => {
    if (!latestJob) {
      return;
    }

    setCurrentImportJob({
      id: latestJob.jobScheduleId,
      name: latestJob.mediaUrls[0]?.fileName ?? getImportJobName(latestJob.createdAt),
    });
  }, [latestJob]);

  const onBulkUpload = useCallback(
    async (media?: CreateMediaInput) => {
      if (media?.url) {
        const res = await executeCreateImportJob({
          input: {
            purpose,
            mediaUrls: [
              {
                format: fileType,
                url: decodeURIComponent(media.url),
                fileName: media.name ?? '',
              },
            ],
            input: jobInputProps,
          },
        });

        if (res.error) {
          showDefaultErrorSnackbar();
        }

        setCurrentImportJob({
          id: res.data!.createImportJob!.jobScheduleId,
          name: media.name ?? '',
        });
      }
    },
    [executeCreateImportJob, jobInputProps, purpose, showDefaultErrorSnackbar],
  );

  const { uppy } = useMediaUpload(referenceId ?? purpose, onBulkUpload);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const showFileSelector = useCallback(() => {
    if (latestJob) {
      showErrorSnackbar({ message: t('Common:BulkImport.ImportInProgressError') });
      return;
    }
    fileInputRef.current?.click();
  }, [latestJob, showErrorSnackbar, t]);

  const onClear = useCallback(() => {
    setCurrentImportJob(undefined);
  }, []);

  return {
    fileInputRef,
    showFileSelector,
    uppy,
    currentImportJob,
    refetchJobs,
    onClear,
  };
};

export default useImportProps;
